import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import { Dashboard, PageLayout, TabPills } from '@components/common'
import { useActiveTab } from '@components/hooks/useActiveTab'
import { BackButton, HeadContent } from '@fourthwall/components'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'
import { routing } from '@utils/routing'

import * as S from './PlatformAppEdit.styled'
import { usePlatformAppClient } from './hooks/usePlatformAppClient'
import { AdminTab } from './modules/AdminTab'
import { BasicInformationTab } from './modules/BasicInformationTab/BasicInformationTab'
import { OAuthTab } from './modules/OAuthTab/OAuthTab'
import { ScopesTab } from './modules/ScopesTab/ScopesTab'
import { SectionsTab } from './modules/SectionsTab/SectionsTab'
import type { App, AppScopeType } from './types'

export const PlatformAppEdit = () => {
  const { appId } = useParams<{ appId: string }>()

  const { data: platformAppClient, isLoading } = usePlatformAppClient(appId || '')

  const adminMode = useAdminModeContext()
  const isAdmin = !!adminMode?.isActive

  const TABS = [
    { label: 'Basic Information', value: 'basic' },
    { label: 'OAuth', value: 'oauth' },
    { label: 'Scopes', value: 'scopes' },
    { label: 'Sections', value: 'sections' },
    ...(isAdmin ? [{ label: 'Admin', value: 'admin', isAdminOnly: true }] : []),
  ]

  const { activeTab, setTabParam } = useActiveTab({
    tabValues: TABS.map((tab) => tab.value),
  })

  const app = useMemo(() => {
    if (!platformAppClient || !appId) return null

    return {
      ...platformAppClient,
      name: platformAppClient.displayName,
      scopes: platformAppClient.scopes.map((scope) => scope.id as AppScopeType),
      sections: platformAppClient.sections.map((section) => ({
        ...section,
        settings: section.settings.map((setting) => ({
          ...setting,
          // For drag / drop to work, we need to ensure that there is a unique id for each setting
          // SR API does not have a unique id for header types
          id: setting.type === 'header' ? uuidv4() : setting.id,
        })),
      })),
      createdAt: new Date(),
      updatedAt: new Date(),
    } as App
  }, [platformAppClient, appId])

  if (isLoading) {
    return (
      <>
        <HeadContent title="Loading App..." />
        <div>Loading...</div>
      </>
    )
  }

  if (!app) {
    return (
      <>
        <HeadContent title="App Not Found" />
        <div>App not found</div>
      </>
    )
  }

  return (
    <PageLayout>
      <Dashboard.Section
        name={`${app.name}`}
        topContent={<BackButton label="Back" to={routing.settings.platformApps.self} />}
      >
        <TabPills options={TABS} value={activeTab} onChange={setTabParam} />

        <S.TabContent>
          {activeTab === 'basic' && <BasicInformationTab app={app} />}
          {activeTab === 'oauth' && <OAuthTab app={app} />}
          {activeTab === 'scopes' && <ScopesTab app={app} />}
          {activeTab === 'sections' && <SectionsTab app={app} />}
          {activeTab === 'admin' && isAdmin && <AdminTab app={app} />}
        </S.TabContent>
      </Dashboard.Section>
    </PageLayout>
  )
}
